import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from './components/BlacksmithWorkshop';
import ClaimPage from './pages/ClaimPage';
import LegacyMembers from './pages/LegacyMembers';
import AdminPanel from './pages/AdminPanel';
import MintPage from './pages/MintPage';
import AdminPage from './pages/AdminPage';
import LayerUploadPage from './pages/LayerUploadPage';
import WalletPage from './pages/WalletPage';
import ProfilePage from './pages/ProfilePage';
import LimitedEditionPage from './pages/LimitedEditionPage';
import ShopPage from './pages/ShopPage';
import FarmPage from './pages/FarmPage';
import DAOPage from './pages/DAOPage';
import ConnectWalletModal from './components/ConnectWalletModal';
import './App.css';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/useAuth';
import { createICSpicyActor } from './services/icspicy';
import icSpicyLogo from './icspicylogo.PNG';
import spicyBanner from './spicy_banner.svg';

function HomePage() {
  const { principal, isAuthenticated, logout, agent } = useAuth();
  const [icpBalance, setIcpBalance] = useState<number>(0);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [farmStats, setFarmStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    loadFarmStats();
  }, [agent]);

  const loadFarmStats = async () => {
    if (!agent) return;
    setLoadingStats(true);
    try {
      const actor = await createICSpicyActor(agent);
      const stats = await actor.get_farm_stats();
      setFarmStats(stats);
    } catch (err) {
      console.error('Failed to load farm stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleConnect = () => {
    setShowConnectModal(true);
  };

  const handleDisconnect = async () => {
    await logout();
    setIcpBalance(0);
  };

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        {/* IC SPICY Hero Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-12 shadow-2xl"
        >
          <img 
            src={spicyBanner} 
            alt="IC SPICY" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center z-10">
              <motion.img 
                src={icSpicyLogo} 
                alt="IC SPICY Logo" 
                className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 rounded-2xl border-2 border-red-500 shadow-2xl"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
                <span className="text-white">IC</span>{' '}
                <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                  SPICY
                </span>
              </h1>
              <p className="text-red-400 text-lg md:text-xl mb-2">RWA Co-op | Fresh Florida Peppers 🌶️</p>
              <p className="text-gray-300 text-sm md:text-base">Where Digital Art Meets Real-World Assets</p>
            </div>
          </div>
        </motion.div>

        {/* Farm Stats */}
        {farmStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark rounded-2xl p-6 mb-6 max-w-4xl mx-auto"
          >
            <h2 className="text-2xl font-semibold text-gold mb-4 text-center">Farm Statistics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Members</p>
                <p className="text-2xl font-bold text-gold">{Number(farmStats.members)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Pepper Plants</p>
                <p className="text-2xl font-bold text-gold">{Number(farmStats.pepper_plants)}</p>
              </div>
              {farmStats.co2_offset && (
                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-1">CO₂ Offset</p>
                  <p className="text-lg font-bold text-gold">{farmStats.co2_offset}</p>
                </div>
              )}
              {farmStats.harvest_yield && (
                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-1">Harvest Yield</p>
                  <p className="text-lg font-bold text-gold">{farmStats.harvest_yield}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gold via-yellow-500 to-gold bg-clip-text text-transparent">
            THE FORGE
          </h2>
          <p className="text-xl text-gray-300 mb-2">IC Spicy RWA NFT Minter</p>
        </motion.div>

        {/* Wallet Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark rounded-2xl p-6 mb-6 max-w-2xl mx-auto"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gold">Wallet</h2>
            {isAuthenticated ? (
              <button
                onClick={handleDisconnect}
                className="text-gray-400 hover:text-white transition"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnect}
                className="bg-gold hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded-lg transition-all"
              >
                Connect Wallet
              </button>
            )}
          </div>
          
          {isAuthenticated && principal && (
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-400">Principal ID</p>
                <p className="text-sm font-mono break-all bg-black/30 p-2 rounded">
                  {principal.toText()}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-400">ICP Balance</p>
                <p className="text-2xl font-bold text-gold">
                  {icpBalance.toFixed(4)} ICP
                </p>
              </div>
            </div>
          )}
        </motion.div>

        <ConnectWalletModal
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
          showLinkWallets={false}
        />

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-6">
          {/* Upload Layers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/upload">
              <div className="text-4xl mb-3">📤</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Upload Layers</h2>
              <p className="text-sm text-gray-400">Upload 10 layer directories</p>
            </Link>
          </motion.div>

          {/* Mint NFT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/mint">
              <div className="text-4xl mb-3">🎨</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Mint NFT</h2>
              <p className="text-sm text-gray-400">Mint unique generative NFTs</p>
            </Link>
          </motion.div>

          {/* Wallet */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/wallet">
              <div className="text-4xl mb-3">💼</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Wallet</h2>
              <p className="text-sm text-gray-400">View and transfer NFTs</p>
            </Link>
          </motion.div>

          {/* Profile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/profile">
              <div className="text-4xl mb-3">👤</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Profile</h2>
              <p className="text-sm text-gray-400">Manage your profile</p>
            </Link>
          </motion.div>

          {/* Limited Edition */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/limited-edition">
              <div className="text-4xl mb-3">⚔️</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Anvil NFTs</h2>
              <p className="text-sm text-gray-400">20 Limited Edition NFTs</p>
            </Link>
          </motion.div>

          {/* Shop */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/shop">
              <div className="text-4xl mb-3">🛒</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Shop</h2>
              <p className="text-sm text-gray-400">Buy fresh peppers</p>
            </Link>
          </motion.div>

          {/* Farm */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/farm">
              <div className="text-4xl mb-3">🌶️</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Farm</h2>
              <p className="text-sm text-gray-400">View farm stats</p>
            </Link>
          </motion.div>

          {/* DAO */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/dao">
              <div className="text-4xl mb-3">🗳️</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">DAO</h2>
              <p className="text-sm text-gray-400">Governance voting</p>
            </Link>
          </motion.div>

          {/* Admin Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            className="glass-dark rounded-2xl p-6 text-center cursor-pointer"
          >
            <Link to="/admin-page">
              <div className="text-4xl mb-3">⚙️</div>
              <h2 className="text-2xl font-bold mb-2 text-gold">Admin</h2>
              <p className="text-sm text-gray-400">Configure canister</p>
            </Link>
          </motion.div>
        </div>
      </div>
    </BlacksmithWorkshop>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/upload" element={<LayerUploadPage />} />
          <Route path="/mint" element={<MintPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/limited-edition" element={<LimitedEditionPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/farm" element={<FarmPage />} />
          <Route path="/dao" element={<DAOPage />} />
          <Route path="/admin-page" element={<AdminPage />} />
          <Route path="/claim/:tokenId" element={<ClaimPage />} />
          <Route path="/legacy-members" element={<LegacyMembers />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/collection/ic-spicy" element={<div>IC Spicy Collection Page</div>} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
